// configure axios create
import axios from "axios";
import Cookies from "js-cookie";

console.log("API URL BaseURl:", process.env.REACT_APP_API_URL);

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

const getToken = () => {
  const sessionData = sessionStorage.getItem("userInfo");
  if (sessionData) {
    try {
      return JSON.parse(sessionData).token;
    } catch {
      return null;
    }
  }
  const cookieData = Cookies.get("userToken");
  if (cookieData) {
    try {
      return JSON.parse(cookieData).token;
    } catch {
      return null;
    }
  }
  return null;
};

const getAuthorizationHeader = () => `Bearer ${getToken()}`;

const authApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

const authImageApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "multipart/form-data",
    type: "formData",
  },
});

// Attach fresh token on every request
authApi.interceptors.request.use(
  (config) => {
    config.headers.Authorization = getAuthorizationHeader();
    return config;
  },
  (error) => Promise.reject(error)
);

authImageApi.interceptors.request.use(
  (config) => {
    config.headers.Authorization = getAuthorizationHeader();
    return config;
  },
  (error) => Promise.reject(error)
);

// Token rotation — queue concurrent requests while a refresh is in flight
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const forceLogout = () => {
  sessionStorage.removeItem("userInfo");
  Cookies.remove("userToken");
  window.location.href = "/login";
};

const updateStoredToken = (newToken) => {
  const sessionData = sessionStorage.getItem("userInfo");
  if (sessionData) {
    try {
      const parsed = JSON.parse(sessionData);
      parsed.token = newToken;
      sessionStorage.setItem("userInfo", JSON.stringify(parsed));
    } catch {
      /* ignore */
    }
  }
  const cookieData = Cookies.get("userToken");
  if (cookieData) {
    try {
      const parsed = JSON.parse(cookieData);
      parsed.token = newToken;
      Cookies.set("userToken", JSON.stringify(parsed), { expires: 7 });
    } catch {
      /* ignore */
    }
  }
};

const handleUnauthorized = async (error) => {
  const originalRequest = error.config;

  if (!error.response || error.response.status !== 401) {
    return Promise.reject(error);
  }

  const responseData = error.response.data;

  // Device session was explicitly revoked — hard logout, no refresh attempt
  if (responseData?.data === "force_logout") {
    forceLogout();
    return Promise.reject(error);
  }

  // Prevent infinite retry loop
  if (originalRequest._retry) {
    forceLogout();
    return Promise.reject(error);
  }

  // Queue subsequent 401s while a refresh is already in progress
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    })
      .then((token) => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return axios(originalRequest);
      })
      .catch((err) => Promise.reject(err));
  }

  originalRequest._retry = true;
  isRefreshing = true;

  try {
    const { data } = await axios.post(
      `${process.env.REACT_APP_API_URL}/users/refresh-jwt-token`,
      {},
      { headers: { Authorization: getAuthorizationHeader() } }
    );
    const newToken = data.token;
    updateStoredToken(newToken);
    processQueue(null, newToken);
    originalRequest.headers.Authorization = `Bearer ${newToken}`;
    return axios(originalRequest);
  } catch (refreshError) {
    processQueue(refreshError, null);
    forceLogout();
    return Promise.reject(refreshError);
  } finally {
    isRefreshing = false;
  }
};

authApi.interceptors.response.use((response) => response, handleUnauthorized);
authImageApi.interceptors.response.use(
  (response) => response,
  handleUnauthorized
);

export { api, authApi, authImageApi, getAuthorizationHeader };
